import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { PricingCard } from '../../components/SubscriptionComponents';
import SEO from '../../components/SEO/SEO';

const SubscriptionPage = () => {
    const [plans, setPlans] = useState([]);
    const [currentTier, setCurrentTier] = useState('free');
    const [usage, setUsage] = useState(null);
    const [loading, setLoading] = useState(false);
    const [billingPeriod, setBillingPeriod] = useState('monthly');
    const navigate = useNavigate();

    useEffect(() => {
        loadPlans();
        loadUsage();
    }, []);

    const loadPlans = async () => {
        try {
            const response = await fetch(
                `${process.env.REACT_APP_API_URL}/api/v1/subscription/plans`
            );
            const data = await response.json();

            const plansWithHighlight = data.plans.map(plan => ({
                ...plan,
                highlighted: plan.tier === 'premium'
            }));

            setPlans(plansWithHighlight);
        } catch (error) {
            console.error('Failed to load plans:', error);
        }
    };

    const loadUsage = async () => {
        try {
            const token = localStorage.getItem('access_token');

            if (!token) {
                return;
            }

            const response = await fetch(
                `${process.env.REACT_APP_API_URL}/api/v1/subscription/usage`,
                {
                    headers: {
                        Authorization: `Bearer ${token}`
                    }
                }
            );

            if (response.ok) {
                const data = await response.json();
                setUsage(data);
                setCurrentTier(data.tier);
            }
        } catch (error) {
            console.error('Failed to load usage:', error);
        }
    };

    const handleSubscribe = async (tier) => {
        const token = localStorage.getItem('access_token');

        if (!token) {
            navigate('/register');
            return;
        }

        if (tier === 'free') {
            alert('Jūs jau naudojate nemokamą planą!');
            return;
        }

        setLoading(true);

        try {
            const response = await fetch(
                `${process.env.REACT_APP_API_URL}/api/v1/subscription/create-checkout`,
                {
                    method: 'POST',
                    headers: {
                        Authorization: `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify({
                        tier,
                        billing_period: billingPeriod
                    })
                }
            );

            if (response.ok) {
                const data = await response.json();
                window.location.href = data.url;
            } else {
                const error = await response.json();
                alert(
                    `Klaida: ${
                        error.detail || 'Nepavyko sukurti checkout sesijos'
                    }`
                );
            }
        } catch (error) {
            console.error('Checkout error:', error);
            alert('Įvyko klaida. Bandykite dar kartą.');
        } finally {
            setLoading(false);
        }
    };

    const handleManageSubscription = async () => {
        setLoading(true);

        try {
            const token = localStorage.getItem('access_token');

            const response = await fetch(
                `${process.env.REACT_APP_API_URL}/api/v1/subscription/portal`,
                {
                    method: 'POST',
                    headers: {
                        Authorization: `Bearer ${token}`
                    }
                }
            );

            if (response.ok) {
                const data = await response.json();
                window.location.href = data.url;
            } else {
                alert('Nepavyko atidaryti valdymo portalo');
            }
        } catch (error) {
            console.error('Portal error:', error);
            alert('Įvyko klaida');
        } finally {
            setLoading(false);
        }
    };

    const featureNames = {
        basic_questions: 'Pagrindiniai klausimai',
        hints: 'Užuominos',
        explanations: 'Paaiškinimai',
        personalized: 'Personalizuoti pratimai',
        adaptive: 'Adaptyvūs klausimai',
        stories: 'Pasakojimai su klausimais',
        priority: 'Prioritetinė pagalba',
        all: 'Visos funkcijos'
    };

    return (
        <>
            <SEO
                title="SmartPanda kainos – Mokomieji žaidimai vaikams"
                description="Pasirinkite SmartPanda planą ir suteikite vaikui interaktyvų mokymąsi su AI. Mokomieji žaidimai matematikai, kalbai, skaitymui ir kitoms žinioms."
                canonical="/subscription"
            />

            <div className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-blue-50 py-12 px-4">
                <div className="max-w-7xl mx-auto">

                    {/* Header */}
                    <div className="text-center mb-12">
                        <button
                            onClick={() => navigate(-1)}
                            className="mb-6 px-6 py-2 bg-white rounded-xl hover:bg-gray-100 transition font-bold shadow-md"
                        >
                            ← Atgal
                        </button>

                        <h1 className="text-5xl font-bold text-gray-800 mb-4">
                            🚀 Prenumeratos Planai
                        </h1>

                        <p className="text-xl text-gray-600 mb-8">
                            Pasirinkite planą, kuris geriausiai atitinka jūsų poreikius
                        </p>

                        {/* Billing Period Toggle */}
                        <div className="flex justify-center mb-12">
                            <div className="bg-white p-1.5 rounded-2xl shadow-lg inline-flex items-center border border-purple-100">
                                {[
                                    { id: 'monthly', label: 'Mėnesio' },
                                    { id: 'half_yearly', label: '6 Mėnesiai', badge: '-15%' },
                                    { id: 'yearly', label: 'Metų', badge: '-25%' }
                                ].map((period) => (
                                    <button
                                        key={period.id}
                                        onClick={() => setBillingPeriod(period.id)}
                                        className={`
                                            relative px-8 py-3 rounded-xl transition-all duration-300 font-bold text-sm
                                            ${
                                                billingPeriod === period.id
                                                    ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white shadow-md scale-105'
                                                    : 'text-gray-500 hover:text-gray-800 hover:bg-gray-50'
                                            }
                                        `}
                                    >
                                        {period.label}

                                        {period.badge && (
                                            <span
                                                className={`
                                                    absolute -top-3 -right-2 px-2 py-1 rounded-full text-[10px] uppercase tracking-wider
                                                    ${
                                                        billingPeriod === period.id
                                                            ? 'bg-green-500 text-white'
                                                            : 'bg-purple-100 text-purple-600'
                                                    }
                                                `}
                                            >
                                                {period.badge}
                                            </span>
                                        )}
                                    </button>
                                ))}
                            </div>
                        </div>

                        {/* Current Usage Stats */}
                        {usage && (
                            <div className="bg-white rounded-2xl shadow-xl p-6 max-w-md mx-auto mb-8">
                                <div className="space-y-4 mb-6">
                                    <div className="flex justify-between items-center">
                                        <div>
                                            <p className="text-sm font-bold text-gray-700">
                                                Dabartinis planas
                                            </p>
                                            <p className="text-2xl font-black text-purple-600 capitalize">
                                                {usage.tier}
                                            </p>
                                        </div>

                                        <div className="text-right">
                                            <p className="text-sm font-bold text-gray-700">
                                                Šiandien liko užklausų
                                            </p>
                                            <p className="text-2xl font-black text-blue-600">
                                                {usage.daily_remaining}/
                                                {usage.daily_limit === -1
                                                    ? '∞'
                                                    : usage.daily_limit}
                                            </p>
                                        </div>
                                    </div>

                                    <div>
                                        <div className="flex justify-between text-xs font-black mb-2 uppercase tracking-wider text-gray-600">
                                            <span>Mėnesio Tokenų Biudžetas</span>
                                            <span>
                                                {Math.round(
                                                    ((usage?.monthly_tokens || 0) /
                                                        (usage?.monthly_tokens_limit || 1)) *
                                                        100
                                                ) || 0}
                                                %
                                            </span>
                                        </div>

                                        <div className="w-full bg-gray-100 rounded-full h-4 overflow-hidden shadow-inner">
                                            <div
                                                className="bg-gradient-to-r from-blue-500 to-purple-500 h-4 rounded-full transition-all duration-1000"
                                                style={{
                                                    width: `${
                                                        usage?.monthly_tokens_limit === -1
                                                            ? 100
                                                            : Math.min(
                                                                  100,
                                                                  ((usage?.monthly_tokens || 0) /
                                                                      (usage?.monthly_tokens_limit || 1)) *
                                                                      100
                                                              )
                                                    }%`
                                                }}
                                            />
                                        </div>

                                        <p className="text-xs font-black text-gray-600 mt-2 text-right">
                                            Sunaudota{' '}
                                            {(usage?.monthly_tokens || 0).toLocaleString()}{' '}
                                            iš{' '}
                                            {usage?.monthly_tokens_limit === -1
                                                ? 'Neribota'
                                                : (
                                                      usage?.monthly_tokens_limit || 0
                                                  ).toLocaleString()}
                                        </p>
                                    </div>
                                </div>

                                {currentTier !== 'free' && (
                                    <button
                                        onClick={handleManageSubscription}
                                        disabled={loading}
                                        className="mt-4 w-full py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition font-bold disabled:opacity-50"
                                    >
                                        Valdyti prenumeratą
                                    </button>
                                )}
                            </div>
                        )}
                    </div>

                    {/* Pricing Cards */}
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-12">
                        {plans.map((plan) => (
                            <PricingCard
                                key={plan.tier}
                                plan={{
                                    ...plan,
                                    price:
                                        plan.tier === 'free'
                                            ? 0
                                            : (
                                                  plan.prices?.[billingPeriod]?.price ??
                                                  plan.price ??
                                                  0
                                              ),
                                    period:
                                        billingPeriod === 'monthly'
                                            ? 'mėn'
                                            : billingPeriod === 'half_yearly'
                                            ? '6 mėn'
                                            : 'metai'
                                }}
                                currentTier={currentTier}
                                onSubscribe={handleSubscribe}
                                loading={loading}
                                featureNames={featureNames}
                            />
                        ))}
                    </div>

                    {/* FAQ Section */}
                    <div className="bg-white rounded-3xl shadow-2xl p-12 mb-12">
                        <h2 className="text-3xl font-bold text-center text-gray-800 mb-8">
                            ❓ Dažnai Užduodami Klausimai
                        </h2>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                            <div>
                                <h3 className="font-bold text-lg text-gray-800 mb-2">
                                    Ar galiu atšaukti bet kada?
                                </h3>
                                <p className="text-gray-600">
                                    Taip! Galite atšaukti prenumeratą bet kada. Ji veiks iki periodo pabaigos.
                                </p>
                            </div>

                            <div>
                                <h3 className="font-bold text-lg text-gray-800 mb-2">
                                    Kaip veikia nemokamas planas?
                                </h3>
                                <p className="text-gray-600">
                                    Nemokamas planas leidžia naudoti 10 AI užklausų per dieną. Nereikia mokėti!
                                </p>
                            </div>

                            <div>
                                <h3 className="font-bold text-lg text-gray-800 mb-2">
                                    Ar galiu pakeisti planą?
                                </h3>
                                <p className="text-gray-600">
                                    Taip! Galite bet kada pagerinti arba sumažinti savo planą.
                                </p>
                            </div>

                            <div>
                                <h3 className="font-bold text-lg text-gray-800 mb-2">
                                    Kokie mokėjimo būdai priimami?
                                </h3>
                                <p className="text-gray-600">
                                    Priimame visas pagrindines kredito ir debeto korteles per Stripe.
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* Trust Badges */}
                    <div className="text-center">
                        <p className="text-gray-600 mb-4">
                            Saugus mokėjimas per
                        </p>

                        <div className="flex justify-center items-center gap-8">
                            <div className="text-4xl">🔒</div>
                            <div className="text-2xl font-bold text-gray-700">
                                Stripe
                            </div>
                            <div className="text-4xl">✓</div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default SubscriptionPage;
